import React, { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import {
  apiBase,
  dateLabel,
  jurisdictionLabel,
  ReviewApiError,
  topicLabel,
} from "../Reviews/api";
import "../Reviews/reviews.css";

async function adminApi<T = any>(
  path: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBase}/api/admin${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new ReviewApiError(
      response.status,
      result.message || result.error || "Could not complete this request"
    );
  return result;
}

function useAdminList<T>(path: string, key: string, status: string) {
  const [rows, setRows] = useState<T[]>([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setError("");
    adminApi(`${path}?status=${status}`, "GET", undefined, c.signal)
      .then(d => setRows(d[key]))
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [path, key, status, revision]);
  return { rows, error, loading, reload: () => setRevision(r => r + 1) };
}

function StatusFilter({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <Form.Select
      aria-label="Filter by status"
      className="w-auto"
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </Form.Select>
  );
}

function EmailDelivery({
  status,
  attempts,
  recovery,
}: {
  status?: string;
  attempts?: number;
  recovery?: string;
}) {
  if (!status) return null;
  if (status === "failed" || status === "skipped")
    return (
      <Alert variant="warning">
        Email {status === "failed" ? "delivery failed" : "was not sent"}.
        Contact this person to confirm their account or application status.{recovery && ` ${recovery}`}
      </Alert>
    );
  const label =
    status === "sent"
      ? "Email delivered to mail server"
      : status === "sending"
        ? "Email sending"
        : (attempts || 0) > 0
          ? "Email delivery retrying"
          : "Email queued";
  return <p className="text-secondary mb-2">{label}</p>;
}

export function AccountRequests() {
  const [status, setStatus] = useState("pending");
  const { rows, error, loading, reload } = useAdminList<any>(
    "/account-requests",
    "requests",
    status
  );
  const [busy, setBusy] = useState(""),
    [notice, setNotice] = useState(""),
    [actionError, setActionError] = useState("");
  async function decide(r: any, decision: "approve" | "decline") {
    if (
      decision === "decline" &&
      !window.confirm(`Decline the account request from ${r.email}?`)
    )
      return;
    setBusy(r.id);
    setNotice("");
    setActionError("");
    try {
      const result = await adminApi(
        `/account-requests/${r.id}/${decision}`,
        "POST",
        {}
      );
      setNotice(
        decision === "approve"
          ? result.emailQueued
            ? `${r.email} is approved. Their welcome email is queued for delivery.`
            : `${r.email} is approved, but the email could not be queued. Let them know they can sign in.`
          : `${r.email} was declined. Their decision email is queued for delivery.`
      );
      reload();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setBusy("");
    }
  }
  return (
    <main className="reviewed-documents review-page">
      <header className="reviewed-documents-heading mt-4">
        <div>
          <p className="reviewed-eyebrow">OPERATIONS</p>
          <h1>Account requests</h1>
          <p>
            Reviewed is approval-only: people who request an account can sign in
            once you approve them. Approving emails them a sign-in link.
          </p>
        </div>
        <div className="review-header-actions">
          <StatusFilter
            value={status}
            onChange={setStatus}
            options={[
              ["pending", "Awaiting approval"],
              ["approved", "Approved"],
              ["declined", "Declined"],
              ["all", "All"],
            ]}
          />
          <Button variant="light" onClick={reload}>
            Refresh
          </Button>
        </div>
      </header>
      {notice && <Alert variant="success">{notice}</Alert>}
      {(error || actionError) && (
        <Alert variant="danger">{actionError || error}</Alert>
      )}
      {loading ? (
        <p>Loading account requests…</p>
      ) : !rows.length ? (
        <section className="reviewed-empty">
          <h2>No {status === "all" ? "" : status} account requests</h2>
          <p>New requests from the sign-up page appear here.</p>
        </section>
      ) : (
        rows.map(r => (
          <article className="review-section" key={r.id}>
            <div className="review-card-top">
              <h2>{r.email}</h2>
              <span className="review-badge">
                {r.status === "pending" ? "awaiting approval" : r.status}
              </span>
            </div>
            <p className="text-secondary mb-2">
              Requested {dateLabel(r.created_at)}
              {r.decided_at &&
                ` · ${r.status} ${dateLabel(r.decided_at)}${r.decided_by_email ? ` by ${r.decided_by_email}` : ""}`}
            </p>
            <EmailDelivery
              status={r.email_status}
              attempts={r.email_attempts}
              recovery={
                r.status === "approved"
                  ? "They can sign in with their existing password."
                  : undefined
              }
            />
            {r.status === "pending" && (
              <div className="review-header-actions">
                <Button disabled={!!busy} onClick={() => decide(r, "approve")}>
                  {busy === r.id ? "Saving…" : "Approve"}
                </Button>
                <Button
                  variant="outline-danger"
                  disabled={!!busy}
                  onClick={() => decide(r, "decline")}
                >
                  Decline
                </Button>
              </div>
            )}
          </article>
        ))
      )}
    </main>
  );
}

export function AttorneyApplications() {
  const [status, setStatus] = useState("pending");
  const { rows, error, loading, reload } = useAdminList<any>(
    "/attorney-applications",
    "applications",
    status
  );
  const [notice, setNotice] = useState("");
  return (
    <main className="reviewed-documents review-page">
      <header className="reviewed-documents-heading mt-4">
        <div>
          <p className="reviewed-eyebrow">OPERATIONS</p>
          <h1>Attorney applications</h1>
          <p>
            Submissions from the public “Work with us” page. Verify each
            admission, then create the attorney’s account; they receive an email
            to set their password.
          </p>
        </div>
        <div className="review-header-actions">
          <StatusFilter
            value={status}
            onChange={setStatus}
            options={[
              ["pending", "To review"],
              ["accepted", "Accepted"],
              ["declined", "Declined"],
              ["all", "All"],
            ]}
          />
          <Button variant="light" onClick={reload}>
            Refresh
          </Button>
        </div>
      </header>
      {notice && <Alert variant="success">{notice}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <p>Loading applications…</p>
      ) : !rows.length ? (
        <section className="reviewed-empty">
          <h2>No {status === "all" ? "" : status} applications</h2>
          <p>Applications from the “Work with us” page appear here.</p>
        </section>
      ) : (
        rows.map(a => (
          <Application
            key={a.id}
            a={a}
            onDecided={message => {
              setNotice(message);
              reload();
            }}
          />
        ))
      )}
    </main>
  );
}

function Application({
  a,
  onDecided,
}: {
  a: any;
  onDecided: (message: string) => void;
}) {
  const [notes, setNotes] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const p = a.profile;
  async function decide(decision: "accept" | "decline") {
    if (
      decision === "decline" &&
      !window.confirm(`Decline ${a.full_name}'s application?`)
    )
      return;
    setBusy(true);
    setError("");
    try {
      const result = await adminApi(
        `/attorney-applications/${a.id}/${decision}`,
        "POST",
        decision === "accept"
          ? { verificationNotes: notes }
          : notes.trim()
            ? { note: notes }
            : {}
      );
      onDecided(
        decision === "accept"
          ? result.emailQueued
            ? `${a.full_name}'s attorney account was created and their password setup email is queued for delivery.`
            : `${a.full_name}'s attorney account was created, but the email could not be queued. They can use “Reset password” with ${a.email}.`
          : `${a.full_name}'s application was declined. Their decision email is queued for delivery.`
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <article className="review-section">
      <div className="review-card-top">
        <h2>{a.full_name}</h2>
        <span className="review-badge">{a.status}</span>
      </div>
      <p className="text-secondary mb-2">
        {a.email}
        {a.phone && ` · ${a.phone}`} · {p.experienceYears} years · applied{" "}
        {dateLabel(a.created_at)}
        {/^https?:\/\//i.test(a.website || "") && (
          <>
            {" · "}
            <a href={a.website} target="_blank" rel="noreferrer noopener">
              website
            </a>
          </>
        )}
      </p>
      <EmailDelivery
        status={a.email_status}
        attempts={a.email_attempts}
        recovery={
          a.status === "accepted"
            ? "They can use Reset password with their application email."
            : undefined
        }
      />
      {p.bio && <p>{p.bio}</p>}
      <p className="mb-1">
        <strong>Languages:</strong> {p.languages.join(", ")}
      </p>
      <p className="mb-1">
        <strong>Admissions:</strong>
      </p>
      <ul>
        {p.admissions.map((ad: any, i: number) => (
          <li key={i}>
            {ad.jurisdiction} ({ad.country}) · {ad.authority}
            {ad.licenseNumber && ` · #${ad.licenseNumber}`} · admitted{" "}
            {ad.admittedOn} · {ad.status}
          </li>
        ))}
      </ul>
      <p className="mb-1">
        <strong>Practice coverage:</strong>
      </p>
      <ul>
        {p.coverage.map((c: any, i: number) => (
          <li key={i}>
            {topicLabel(c.topicKey)} · {jurisdictionLabel(c.jurisdiction)}
          </li>
        ))}
      </ul>
      {a.status === "pending" ? (
        <>
          {a.account_exists && (
            <Alert variant="warning">
              An account with {a.email} already exists, so an attorney account
              cannot be created from this application.
            </Alert>
          )}
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group controlId={`application-notes-${a.id}`}>
            <Form.Label>Verification notes (required to accept)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              maxLength={4000}
              placeholder="How you verified the admission, e.g. State Bar record checked on…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </Form.Group>
          <div className="review-header-actions mt-3">
            <Button
              disabled={busy || !notes.trim() || a.account_exists}
              onClick={() => decide("accept")}
            >
              {busy ? "Saving…" : "Create attorney account"}
            </Button>
            <Button
              variant="outline-danger"
              disabled={busy}
              onClick={() => decide("decline")}
            >
              Decline
            </Button>
          </div>
        </>
      ) : (
        <p className="text-secondary mb-0">
          {a.status === "accepted" ? "Accepted" : "Declined"}
          {a.decided_at && ` ${dateLabel(a.decided_at)}`}
          {a.decided_by_email && ` by ${a.decided_by_email}`}
          {a.decision_note && ` · ${a.decision_note}`}
        </p>
      )}
    </article>
  );
}
